import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCircle, Users, MessageCircle, Phone, Mail, Store, X, Settings } from 'lucide-react';
import { Avatar } from '../../ui/Avatar';
import { Button } from '../../ui/Button';
import { navigate } from '../../../App';

/**
 * Read-only modal showing one community member's profile:
 * avatar + name, optional bio, contact methods, and the businesses /
 * events / barters they've created in this zone.
 *
 * If the member is viewing their *own* profile, an "Edit profile" button
 * appears that bubbles up via `onEditProfile` so the parent can swap to
 * the edit modal seamlessly.
 */
export function MemberDetailModal({
    isOpen,
    member,
    chatId,
    onClose,
    onEditProfile,
}) {
    const canEdit = chatId && chatId !== 'guest' && member?.own_profile;

    return (
        <AnimatePresence>
            {isOpen && member && (
                <div
                    className="fixed inset-0 bg-ink/45 backdrop-blur-sm z-modal flex items-center justify-center p-4"
                    onClick={onClose}
                    role="dialog"
                    aria-modal="true"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 16 }}
                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md max-h-[85vh] overflow-y-auto relative bg-surface rounded-2xl shadow-elevated border border-line-subtle"
                    >
                        <span aria-hidden="true" className="absolute top-0 inset-x-0 h-1 bg-brand-500 rounded-t-2xl" />

                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="סגור"
                            className="absolute top-4 end-4 z-10 w-9 h-9 rounded-pill bg-surface/90 hover:bg-surface shadow-sm flex items-center justify-center transition-colors"
                        >
                            <X className="w-4 h-4 text-ink-muted" />
                        </button>

                        <div className="p-8 pt-10">
                            {/* Avatar + name */}
                            <div className="flex flex-col items-center mb-7">
                                <div className="mb-4">
                                    <span className="block rounded-pill p-1 bg-brand-500 shadow-elevated">
                                        <span className="block rounded-pill bg-surface p-0.5">
                                            <Avatar
                                                src={member.photo_url}
                                                name={member.name}
                                                size="2xl"
                                            />
                                        </span>
                                    </span>
                                </div>
                                <h3 className="heading-lg text-center mb-2">{member.name}</h3>
                                <div className="flex items-center gap-2 text-ink-muted text-sm">
                                    <Users className="w-4 h-4 text-brand-500" />
                                    <span>חבר קהילה</span>
                                </div>
                            </div>

                            {member.bio && (
                                <Section icon={<MessageCircle className="w-4 h-4 text-ink-inverse" />} title="קצת עליי">
                                    <p className="text-ink text-sm leading-relaxed">{member.bio}</p>
                                </Section>
                            )}

                            {(member.share_phone || member.share_whatsapp || member.share_email) && (
                                <Section icon={<Phone className="w-4 h-4 text-ink-inverse" />} title="פרטי התקשרות">
                                    <div className="space-y-3">
                                        {member.share_phone && member.phone_number && (
                                            <ContactRow icon={<Phone className="w-4 h-4 text-brand-500" />}>
                                                <a
                                                    href={`tel:${member.phone_number}`}
                                                    className="text-ink text-sm hover:text-brand-700 transition-colors"
                                                >
                                                    {member.phone_number}
                                                </a>
                                            </ContactRow>
                                        )}
                                        {member.share_whatsapp && member.phone_number && (
                                            <ContactRow icon={<MessageCircle className="w-4 h-4 text-success-600" />}>
                                                <a
                                                    href={`https://wa.me/${member.phone_number.replace(/[^0-9]/g, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-ink text-sm hover:text-success-700 transition-colors"
                                                >
                                                    שלח הודעת WhatsApp
                                                </a>
                                            </ContactRow>
                                        )}
                                        {member.share_email && member.email && (
                                            <ContactRow icon={<Mail className="w-4 h-4 text-brand-500" />}>
                                                <a
                                                    href={`mailto:${member.email}`}
                                                    className="text-ink text-sm hover:text-brand-700 transition-colors"
                                                >
                                                    {member.email}
                                                </a>
                                            </ContactRow>
                                        )}
                                    </div>
                                </Section>
                            )}

                            {member.share_entities && member.entities?.length > 0 && (
                                <Section icon={<Store className="w-4 h-4 text-ink-inverse" />} title="העשייה שלי">
                                    <div className="space-y-3">
                                        {member.entities.map((entity, idx) => (
                                            <EntityRow key={idx} entity={entity} chatId={chatId} index={idx} ownProfile={!!member.own_profile} />
                                        ))}
                                    </div>
                                </Section>
                            )}

                            {!member.bio && (!member.entities || member.entities.length === 0) && (
                                <div className="text-center py-10 bg-surface-muted rounded-xl border border-line-subtle">
                                    <div className="w-16 h-16 mx-auto mb-3 rounded-pill bg-brand-50 flex items-center justify-center">
                                        <UserCircle className="w-8 h-8 text-brand-500" />
                                    </div>
                                    <p className="text-ink-muted text-sm font-medium mb-1">
                                        חבר הקהילה עדיין לא שיתף מידע נוסף
                                    </p>
                                    <p className="text-ink-subtle text-xs">בקרוב יספרו לנו עוד!</p>
                                </div>
                            )}

                            <div className="mt-8 space-y-3">
                                <Button variant="primary" fullWidth onClick={onClose}>סגור</Button>
                                {canEdit && (
                                    <Button variant="outline" fullWidth onClick={onEditProfile}>
                                        ערוך פרופיל
                                    </Button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

function Section({ icon, title, children }) {
    return (
        <div className="mb-6 p-5 bg-surface-muted rounded-xl border border-line-subtle shadow-xs">
            <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-pill flex items-center justify-center bg-brand-500 shadow-sm">
                    {icon}
                </span>
                <h4 className="text-base font-bold text-ink">{title}</h4>
            </div>
            <div className="ps-10">{children}</div>
        </div>
    );
}

function ContactRow({ icon, children }) {
    return (
        <div className="flex items-center gap-3">
            <span className="shrink-0">{icon}</span>
            <span className="min-w-0 flex-1">{children}</span>
        </div>
    );
}

function EntityRow({ entity, chatId, index, ownProfile }) {
    const handleClick = () => {
        const entityId = entity.business_id || entity.event_id || entity.barter_id;
        if (entityId) window.location.href = `/?entity=${entityId}&chat_id=${chatId}`;
    };
    const showManage = ownProfile && entity.has_store && entity.store_slug;
    return (
        <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.04 }}
            className="group w-full bg-surface rounded-lg border border-line-subtle hover:border-brand-300 hover:shadow-sm transition-[background-color,border-color,box-shadow]"
        >
            <button
                type="button"
                onClick={handleClick}
                className="w-full flex items-center gap-3 p-4 text-right"
            >
                {entity.image_url ? (
                    <img
                        src={entity.image_url}
                        alt=""
                        aria-hidden="true"
                        className="w-14 h-14 rounded-lg object-cover shadow-xs group-hover:shadow-sm transition-shadow"
                    />
                ) : (
                    <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-surface-muted">
                        <Store className="w-6 h-6 text-brand-500" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-ink text-sm truncate group-hover:text-brand-700 transition-colors">
                        {entity.name}
                    </p>
                    <p className="text-xs text-ink-muted mt-0.5">{entity.category}</p>
                    {entity.avg_rating > 0 && (
                        <span className="inline-block text-xs text-secondary-700 font-semibold mt-1">
                            ⭐ {entity.avg_rating.toFixed(1)}
                        </span>
                    )}
                </div>
            </button>
            {showManage && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate('store-admin', `slug=${entity.store_slug}`);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-t border-line-subtle text-sm font-bold text-brand-700 hover:bg-brand-50 transition-colors rounded-b-lg"
                >
                    <Settings className="w-4 h-4" strokeWidth={2.5} />
                    <span>ניהול חנות</span>
                </button>
            )}
        </motion.div>
    );
}

export default MemberDetailModal;
